/****************************************************
 * Module Name:  kapanSummary.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/

import React, { useState } from "react";
import { DataGridPro, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarExport, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import {
    Box,
    Button,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { useEffect } from "react";
import Select from "react-select";
import CircularProgress from "@mui/material/CircularProgress";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ReportProxy from "../../utility/proxy/manager/reportProxy";
import renderCellExpand from "../common/dataGridCellExpand";
import StoneDetailModal from "../common/stoneDetailModal";
import Utility from "../../utility/utility";
import Constants from "../../utility/constants";
import { LoadingButton } from "@mui/lab";
import CommonSnackbar from "../common/commonSnackBar";
import TotalStockComponent from "../common/totalStockComponent";
import StonestatusProxy from "../../utility/proxy/manager/stonestatusProxy";
import PacketHistoryModal from "../common/packetHistoryModal";
import NoPermission from "../common/noPermission";
import KapanHistoryModal from "../manufacturing/operations/kapanHistoryModal";
import PageTitle from "../common/pageTitle";

const KapanSummary = ({ report, kid, sts }) => {

    const [status, setStatus] = useState(report ? { value: sts, label: sts } : []);
    const [statusOption, setStatusOption] = useState([]);

    const [listData, setListData] = useState([]);
    const [access, setAccess] = useState(false);
    const [kapanid, setKapanId] = useState(report ? kid : "");
    const [ids, setIds] = useState("");
    const [stoneId, setStoneId] = useState("");
    const [showStoneDetailModal, setShowStoneDetailModal] = useState(false);
    const [showPacketDetailModal, setShowPacketDetailModal] = useState(false);
    const [packetId, setPacketId] = useState("");
    const [memoLoader, setMemoLoader] = useState(false);
    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [filterValues, setFilterValues] = useState({});
    const [showKapanDetailModal, setShowKapanDetailModal] = useState(false);

    const defaultOptions = { value: "*", label: "-- ANY --" };

    useEffect(() => {
        PageTitle("REPORT_KAPAN_KAPANSTATUSSUMMARY");
        checkPermission();
    }, []);

    const checkPermission = () => {
        let allowed = Constants.USER_PERMISSION.find(
            (iPermission) =>
                iPermission.permissionid ===
                Constants.PERMISSIONS.REPORT_KAPAN_KAPANSTATUSSUMMARY
        ) !== undefined
        if (!allowed) {
            setAccess(true)
            return <NoPermission />
        } else {
            report && handleSearch(kid, sts)
            getAllStatusData();
        }
    }

    const fullOptionToValue = (selectedData) => {
        return Utility.GetValueArrayWithStarReplace(
            selectedData,
            Utility.GetValueArrayWithoutStar(statusOption)
        );
    };

    const getAllStatusData = () => {
        StonestatusProxy.GetAllStonestatusMini((response) =>
            setAllStatusData(response)
        );
    };

    const setAllStatusData = (response) => {
        let optionsArray = [];
        let selectedOption = [];
        optionsArray.push(defaultOptions);
        response.map((data) => {
            let option = { value: data.status, label: data.status };
            optionsArray.push(option);

            return true;
        });

        if (selectedOption.length === 0) selectedOption.push(defaultOptions);

        setStatusOption(optionsArray);
        setStatus(selectedOption);

    };

    const handleSearch = (kapanid, status) => {
        let data = {
            kapanid: kapanid.split(/[ ,]+/).map(Number),
            status: report ? [status] : fullOptionToValue(status)
        }
        setMemoLoader(true);
        ReportProxy.GetKapanStatusSummary(
            data,
            (response) => {
                if (response) {
                    const uniqueFilterValues = {
                        status: Utility.getDistinctOptions(response, "status"),
                        clarity: Utility.getDistinctOptions(response, "clarity"),
                        color: Utility.getDistinctOptions(response, "color"),
                        cut: Utility.getDistinctOptions(response, "cut"),
                        flo: Utility.getDistinctOptions(response, "flo"),
                        lab: Utility.getDistinctOptions(response, "lab"),
                        location: Utility.getDistinctOptions(response, "location"),
                        country: Utility.getDistinctOptions(response, "country"),
                        pol: Utility.getDistinctOptions(response, "pol"),
                        shape: Utility.getDistinctOptions(response, "shape"),
                        sym: Utility.getDistinctOptions(response, "sym"),
                    };
                    setFilterValues(uniqueFilterValues);
                    setListData(response);
                    setMemoLoader(false);
                } else {
                    setMemoLoader(false);
                    setErrorSnackbar(true);
                }
            }
        );
    };



    const handleStoneDetailModal = (params) => {
        setStoneId(params.value);
        setShowStoneDetailModal(true);
    };

    const handleClose = () => {
        setShowStoneDetailModal(false);
        setShowPacketDetailModal(false)
    };

    const handlePacketDetailModal = (params, kapanid) => {
        setIds(kapanid)
        setPacketId(params);
        setShowPacketDetailModal(true);
    };

    const initialColumns = () => [
        {
            field: "stoneid",
            headerName: "STONEID",
            headerAlign: "center",
            align: "center",
            width: 80,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.NeedToShowStoneDetailModal() ?
                <Button
                    sx={{ textDecorationLine: "underline" }}
                    onClick={() => handleStoneDetailModal(params)}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
        },
        {
            field: "kapanid",
            headerName: "KAPANID",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => (Utility.NeedToShowKapanDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => {
                        setKapanId(params.row.kapanid);
                        setShowKapanDetailModal(true);
                    }}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
            width: 80,
        },
        {
            field: "packetid",
            headerName: "PACKETID",
            headerAlign: "center",
            align: "center",
            width: 80,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => (Utility.NeedToShowPacketDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => handlePacketDetailModal(params.value, params.row.kapanid)}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
        },
        {
            field: "size",
            headerName: "SIZE",
            headerAlign: "center",
            align: "right",
            width: 40,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.size)
        },
        {
            field: "total",
            headerName: "TOTAL",
            headerAlign: "center",
            align: "right",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.total)
        },
        {
            field: "status",
            headerName: "STATUS",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.status : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "clarity",
            headerName: "CLARITY",
            headerAlign: "center",
            align: "center",
            width: 75,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.clarity : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "color",
            headerName: "COLOR",
            headerAlign: "center",
            align: "center",
            width: 65,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.color : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "cut",
            headerName: "CUT",
            headerAlign: "center",
            align: "center",
            width: 50,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.cut : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "createdat",
            headerName: "CREATED AT",
            headerAlign: "center",
            align: "center",
            minWidth: 190,
            type: "dateTime",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
        },
        {
            field: "flo",
            headerName: "FLO",
            headerAlign: "center",
            align: "center",
            width: 60,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.flo : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "lab",
            headerName: "LAB",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.lab : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "locationid",
            headerName: "LOCATION ID",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            minWidth: 120,
        },
        {
            field: "location",
            headerName: "LOCATION",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.location : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "country",
            headerName: "COUNTRY",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.country : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "pol",
            headerName: "POL",
            headerAlign: "center",
            align: "center",
            width: 40,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.pol : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "quality",
            headerName: "QUALITY",
            headerAlign: "center",
            align: "center",
            width: 80,
            renderCell: renderCellExpand,
        },
        {
            field: "shade",
            headerName: "SHADE",
            headerAlign: "center",
            align: "center",
            width: 60,
            renderCell: renderCellExpand,
        },
        {
            field: "shape",
            headerName: "SHAPE",
            headerAlign: "center",
            align: "center",
            width: 80,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.shape : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "sym",
            headerName: "SYM",
            headerAlign: "center",
            align: "center",
            width: 40,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.sym : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "repacode",
            headerName: "REPACODE",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        {
            field: "discount",
            headerName: "DIS",
            headerAlign: "center",
            align: "center",
            width: 50,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "depth",
            headerName: "DEPTH",
            headerAlign: "center",
            align: "center",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "height",
            headerName: "HEIGHT",
            headerAlign: "center",
            align: "center",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "width",
            headerName: "WIDTH",
            headerAlign: "center",
            align: "center",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "certificateid",
            headerName: "CERT",
            headerAlign: "center",
            align: "center",
            width: 100,
            renderCell: renderCellExpand,
        },
        {
            field: "comment",
            headerName: "COMMENT",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        {
            field: "crownheight",
            headerName: "CR.HEIGHT",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "crownangle",
            headerName: "CR.ANG",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "culetsize",
            headerName: "CULET SZ",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "culetcondition",
            headerName: "CULET",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        {
            field: "depthpercent",
            headerName: "DEPTH%",
            headerAlign: "center",
            align: "center",
            width: 75,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "gridlemin",
            headerName: "GIRDLEMIN",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        {
            field: "intensitylongtitle",
            headerName: "INTENSITYLONGTITLE",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        {
            field: "laserinscription",
            headerName: "INS",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        {
            field: "measurements",
            headerName: "MEASUREMENTS",
            headerAlign: "center",
            align: "center",
            width: 140,
            renderCell: renderCellExpand,
        },
        {
            field: "note",
            headerName: "NOTE",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        {
            field: "paviliondepth",
            headerName: "PV.DEPTH",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        {
            field: "tablepercent",
            headerName: "TABLE%",
            headerAlign: "center",
            align: "center",
            width: 75,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "open",
            headerName: "OPEN",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        {
            field: "gridlecondition",
            headerName: "GIRDLE COND",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        {
            field: "gridlepercent",
            headerName: "GIRDLE%",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "pavilionangle",
            headerName: "PV.ANGLE",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        {
            field: "white",
            headerName: "WHITE",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
    ];

    const columns = initialColumns();
    const rows = Utility.GenerateUniqueIdOfResponseForDataGrid(listData);

    const CustomToolbar = () => {
        return (
            <GridToolbarContainer
                sx={{
                    display: "flex",
                    alignItems: "center",
                    marginHorizontal: "5px",
                    "&.MuiDataGrid-toolbarContainer": {
                        padding: "0px 4px !important",
                    },
                }}
            >
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarExport />
                <Box sx={{ flexGrow: 1, display: "block" }} />
                {memoLoader ? (
                    <CircularProgress size="1rem" />
                ) : (
                    <Tooltip title="Reload List">
                        <RefreshOutlinedIcon
                            onClick={() =>
                                handleSearch(kapanid, status)
                            }
                            color="primary"
                            fontSize="medium"
                            sx={{ cursor: "pointer" }}
                        />
                    </Tooltip>
                )}
                <GridToolbarQuickFilter debounceMs={500} />
            </GridToolbarContainer>
        )
    }
    return (
        <div>
            <CommonSnackbar
                show={errorSnackbar}
                message={Constants.APiFailErrorMessage}
                variant={"error"}
                autoHide={Constants.SnackBarCloseTimer}
                title={"Failed"}
                handleClose={() => setErrorSnackbar(false)}
            />
            {showStoneDetailModal ? (
                <StoneDetailModal
                    show={showStoneDetailModal}
                    stoneId={stoneId}
                    handleClose={handleClose}
                />
            ) : null}
            {showPacketDetailModal ? (
                <PacketHistoryModal
                    show={showPacketDetailModal}
                    packetId={packetId}
                    kapanId={ids}
                    close={handleClose}
                />
            ) : null}
            {showKapanDetailModal ? (
                <KapanHistoryModal
                    show={showKapanDetailModal}
                    kapanId={kapanid}
                    close={() => setShowKapanDetailModal(false)}
                />
            ) : null}
            {
                access ? <NoPermission /> : (
                    <Box px={1}>
                        {
                            report ? '' : (
                                <Box
                                    sx={{
                                        display: "flex",
                                        width: "100%",
                                        flexWrap: "wrap",
                                        gap: "15px",
                                        mb: 2,
                                        mt: 2,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            gap: "10px",
                                            alignItems: "center",
                                            minWidth: "440px"
                                        }}
                                    >
                                        <Typography sx={{ whiteSpace: "nowrap" }}>Kapan Id</Typography>
                                        <Box sx={{ width: "100%" }}>
                                            <TextField
                                                label="Enter Comma separated Kapan Ids"
                                                placeholder="Comma separated Kapan Ids"
                                                multiline
                                                fullWidth
                                                value={kapanid}
                                                onChange={(e) =>
                                                    setKapanId(Utility.ConvertNewLineToComma(e.target.value))
                                                }
                                            />
                                        </Box>
                                    </Box>

                                    <Box
                                        sx={{
                                            display: "flex",
                                            gap: "10px",
                                            alignItems: "center",
                                        }}
                                    >
                                        <Typography>Status</Typography>
                                        <Box sx={{ width: "100%" }}>
                                            <Select
                                                blurInputOnSelect={false}
                                                menuPortalTarget={document.body}
                                                styles={Constants.Styles}
                                                closeMenuOnSelect={false}
                                                getOptionLabel={(option) => option.label}
                                                getOptionValue={(option) => option.value}
                                                isMulti
                                                options={statusOption}
                                                value={status}
                                                onChange={(e) => setStatus(e)}
                                            />
                                        </Box>
                                    </Box>

                                    <Box sx={{ display: "flex", alignItems: "center", }}>
                                        <LoadingButton
                                            loading={memoLoader}
                                            loadingPosition="start"
                                            disabled={kapanid.length === 0 || statusOption.length === 0}
                                            variant="contained"
                                            onClick={() => handleSearch(kapanid, status)}
                                            startIcon={<SearchOutlinedIcon size="large" />}
                                        >
                                            Search
                                        </LoadingButton>
                                    </Box>
                                </Box>
                            )}

                        <Box
                            sx={{
                                width: "100%",
                                backgroundColor: '#fff',
                                color: '#000',
                                position: "relative",
                                "& .MuiTablePagination-selectLabel": {
                                    marginTop: "15px",
                                },
                                "& .MuiTablePagination-displayedRows": {
                                    marginTop: "15px",
                                },
                                "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                "& .css-cc8tf1": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                height: "80vh",
                            }}
                        >
                            <DataGridPro
                                disableRowSelectionOnClick={true}
                                getRowHeight={() => "auto"}
                                sx={{
                                    '& .MuiDataGrid-columnHeader': {
                                        color: '#fff',
                                        backgroundColor: 'grey',
                                    },
                                    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                        py: "4px",
                                    },
                                    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                        py: "12px",
                                    },
                                    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                        py: "18px",
                                    },

                                    fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                                }}
                                density="compact"
                                loading={memoLoader}
                                slots={{ toolbar: CustomToolbar }}
                                rows={rows}
                                columns={columns}
                                getRowId={(row) => row.uniqueId}
                                initialState={{
                                    columns: {
                                        columnVisibilityModel: {
                                            // Hide columns id, the other columns will remain visible
                                            labname: false,
                                            location: false,
                                            country: false,
                                        },
                                    },
                                    pagination: {
                                        paginationModel: Constants.pageSize,
                                    },
                                }}
                                pageSizeOptions={Constants.pageSizeOptions}
                                pagination
                                slotProps={{
                                    toolbar: {
                                        showQuickFilter: true,
                                        quickFilterProps: { debounceMs: 500 },
                                    },
                                }}
                            />
                        </Box>
                        <Box height={100}>
                            <TotalStockComponent data={rows} />
                        </Box>
                    </Box>
                )}
        </div>
    );
};

export default KapanSummary;
